import { Loader2 } from 'lucide-react';
import { useId } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogCloseButton,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  confirmVariant?: 'default' | 'destructive';
  pending?: boolean;
  testIdPrefix?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  confirmVariant = 'default',
  pending = false,
  testIdPrefix,
}: ConfirmDialogProps) {
  const titleId = useId();
  const handleClose = () => {
    if (pending) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      ariaLabelledBy={titleId}
    >
      <DialogCloseButton onClose={handleClose} />
      <DialogHeader>
        <DialogTitle id={titleId}>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={handleClose} disabled={pending}>
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant={confirmVariant === 'destructive' ? 'destructive' : 'default'}
          onClick={onConfirm}
          disabled={pending}
          data-testid={testIdPrefix ? `${testIdPrefix}-confirm` : undefined}
        >
          {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
